import React, {useEffect, useRef, useState} from 'react';
import {FaCircleCheck, FaCircleExclamation} from "react-icons/fa6";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import InputPassword from "../../../components/input/input-password.jsx";
import Alert from "../../../components/alert/alert.jsx";
import AuthLayout from "../../../components/auth-layout/auth-layout.jsx";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import loginService from "../../../services/web/login-service.js";
import {translateServerErrorMessage} from "../login/utils/login-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useLoaderData} from "react-router";
import {queryClient} from "../../../services/query-client.js";
import {SESSION_QUERY_KEY} from "../../../services/session.js";

const API_URL = import.meta.env.VITE_API_URL;

const ChangePassword = () => {
    const navigate = navigateHook();
    const loaderData = useLoaderData();

    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [redirectSeconds, setRedirectSeconds] = useState(3);

    const redirectTimeout = useRef();
    const redirectInterval = useRef();

    useEffect(() => {
        return () => {
            if (redirectTimeout.current) clearTimeout(redirectTimeout.current);
            if (redirectInterval.current) clearInterval(redirectInterval.current);
        };
    }, []);

    const handleImageError = (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = getAssetPath("img/sentry-logo3.png");
    };

    const submit = () => {
        if (loading || success) return;
        const cleanedPassword = password.trim();

        if (cleanedPassword !== confirmation.trim()) {
            setErrorMessage("A senha e a confirmação devem ser idênticas.");
            return;
        }

        if (!cleanedPassword || !confirmation.trim()) {
            setErrorMessage("Preencha os campos obrigatórios.");
            return;
        }

        setErrorMessage("");
        setLoading(true);

        const formData = new FormData();
        formData.append("password", cleanedPassword);

        loginService.changePasswordAfterLogin(formData)
            .then(() => {
                setSuccess(true);
                setLoading(false);
                setRedirectSeconds(3);
                queryClient.removeQueries({queryKey: SESSION_QUERY_KEY});

                redirectInterval.current = setInterval(() => {
                    setRedirectSeconds(prev => prev > 1 ? prev - 1 : prev);
                }, 1000);

                redirectTimeout.current = setTimeout(() => {
                    if (redirectInterval.current) clearInterval(redirectInterval.current);
                    navigate("/home");
                }, 3000);
            })
            .catch(error => {
                console.error(error);
                setLoading(false);
                setErrorMessage(translateServerErrorMessage(error.response?.data?.message));
            });
    };

    useGlobalKey("Enter", submit);

    return (
        <AuthLayout>
            <Card className="w-[420px] max-w-full p-7">
                <Card.Body>
                    <div className="flex flex-col items-center mb-4">
                        <img
                            src={`${API_URL}/corporation/entity/image`}
                            alt={"Sentry"}
                            className="max-h-[125px] object-contain"
                            onError={handleImageError}
                        />
                    </div>

                    <div className="text-center mb-4">
                        <h4 className="font-semibold text-[18px] mb-2">Configurar senha</h4>
                        <span className="text-[#ed5565] text-[15px]">A sua senha expirou e precisa ser alterada.</span>
                    </div>

                    <div className="space-y-5">
                        <InputPassword
                            id="new_password"
                            name="password"
                            target="password"
                            passwordComplexity={loaderData?.passwordComplexity ?? false}
                            label="Nova senha *"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        <InputPassword
                            id="new_password_confirm"
                            name="password_confirm"
                            label="Confirmação de senha *"
                            value={confirmation}
                            password={password}
                            onChange={(e) => setConfirmation(e.target.value)}
                        />

                        <Button target="primary" className="w-full" onClick={submit} loading={loading} loadingText="Definindo...">
                            Definir
                        </Button>

                        { errorMessage &&
                            <Alert target="danger" className="flex items-center gap-2 !text-[15px]">
                                <FaCircleExclamation/> {errorMessage}
                            </Alert>
                        }

                        { success &&
                            <Alert target="success" className="flex items-center gap-2 !text-[15px]">
                                <FaCircleCheck/> Senha alterada com sucesso! Você será redirecionado em {redirectSeconds}...
                            </Alert>
                        }
                    </div>
                </Card.Body>
            </Card>
        </AuthLayout>
    );
};

export default ChangePassword;